import type { ThemeConfig } from 'antd';
import { theme as antTheme } from 'antd';
import type { FontFamily } from '@bakedshift/shared';
import { lookupOwn } from './table-lookup';

export type ThemeMode = 'light' | 'dark' | 'coffee' | 'dark-forest' | 'kingdom';

export const THEME_MODES: readonly ThemeMode[] = [
  'light',
  'dark',
  'coffee',
  'dark-forest',
  'kingdom',
] as const;

export function isDarkMode(mode: ThemeMode): boolean {
  return mode === 'dark' || mode === 'dark-forest';
}

// Shared spacing/radius/font tokens
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// UI v2 default UI font — Geist, leading an Arabic-aware fallback stack (SPEC §3.2).
// Used when a workspace hasn't picked a custom branding font.
export const uiFontStack =
  "'Geist', 'Noto Sans Arabic', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Arabic-aware font stack: Noto Sans Arabic inside a generic stack so RTL text stays crisp.
export const fontFamilies: Record<FontFamily, string> = {
  inter:    "'Inter', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  poppins:  "'Poppins', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  roboto:   "'Roboto', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  openSans: "'Open Sans', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  nunito:   "'Nunito', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

/**
 * Elevation. The NAMES live here; the VALUES live in `styles/tokens.css`.
 *
 * This used to be sixteen literal shadows, plus a parallel `darkShadows` of
 * another sixteen. Both are gone. The stylesheet already owns five espresso
 * tokens that match the mockup's `:root` exactly and that every theme block
 * redefines, so a second copy in JS could only ever drift from them — and did,
 * into neutral blacks and four AntD-blue glows on a parchment app.
 *
 * The deciding argument was theme reactivity rather than tidiness. An inline
 * `boxShadow: shadows.md` reads this object ONCE, frozen at import: there are
 * 119 such call sites, and not one of them could reach `darkShadows`, so every
 * card in the two dark themes drew a light-mode shadow. A custom property is
 * re-resolved by the cascade per theme, so the same 119 call sites now follow
 * all five themes without changing a line.
 *
 * Several names deliberately resolve to the same token: the old scale had
 * sixteen steps where the mockup has five, and `xs`/`sm`/`button` were not
 * meaningfully different shadows. Collapsing them is the reconciliation, not a
 * casualty of it. Seven names carrying no call site at all (`2xl`, `inner`,
 * `glow`, `glowLg`, `dropdown`, `modal`, `buttonHover`) were deleted.
 *
 * Add a new step by adding a `--shadow-*` to tokens.css and naming it here.
 * Never inline a literal: `lib/__tests__/shadowTokens.guard.test.ts` fails the
 * build on one, and on a `var()` naming a property the stylesheet never declares
 * (which is not an error — it renders no shadow at all, silently).
 */
export const shadows = {
  xs: 'var(--shadow-sm)',
  sm: 'var(--shadow-sm)',
  md: 'var(--shadow-md)',
  lg: 'var(--shadow-lift)',
  xl: 'var(--shadow-card-hover)',
  card: 'var(--shadow-card)',
  cardHover: 'var(--shadow-card-hover)',
  button: 'var(--shadow-sm)',
  /** A focus RING, not an elevation — see `--ring-accent` in tokens.css. */
  inputFocus: 'var(--ring-accent)',
  /** Tints a control with its own colour rather than raising it. */
  glowAccent: 'var(--glow-accent)',
  glowAccentStrong: 'var(--glow-accent-strong)',
  glowPositive: 'var(--glow-positive)',
  /** A trough. The only inset in the app. */
  inset: 'var(--shadow-inset)',
} as const;

/**
 * Brand gradients, for the same reason the shadows moved: they were retyped as
 * literal hex pairs at five call sites, so a palette change reached none of
 * them. Defined in tokens.css from the accent tokens.
 */
export const gradients = {
  brand: 'var(--gradient-brand)',
  positive: 'var(--gradient-positive)',
} as const;

// Semantic surface tokens per theme — these are the readable, WCAG-AA pairs.
interface ThemeSurface {
  readonly primary: string;
  readonly bgContainer: string;
  readonly bgElevated: string;
  readonly bgLayout: string;
  readonly bgSpotlight: string;
  readonly text: string;
  readonly textSecondary: string;
  readonly textTertiary: string;
  readonly border: string;
  readonly borderSecondary: string;
  readonly fillSecondary: string;
  readonly fillTertiary: string;
  readonly tableHeaderBg: string;
  readonly tableHeaderColor: string;
  readonly tableRowHoverBg: string;
  readonly menuItemSelectedBg: string;
  readonly menuItemHoverBg: string;
  readonly segmentedSelectedBg: string;
  readonly segmentedTrackBg: string;
  readonly tagDefaultBg: string;
  readonly progressRemaining: string;
  readonly dividerColor: string;
  readonly dropdownHoverBg: string;
  readonly defaultBorderColor: string;
  // UI v2 (optional; set on the parchment `light` + `dark` surfaces; legacy
  // coffee/dark-forest/kingdom themes omit them and fall back).
  readonly successColor?: string;
  readonly warningColor?: string;
  readonly siderBg?: string;
  readonly headerBg?: string;
}

// Theme palette — derived from Clearpath THEME.md. Every pair is WCAG-AA validated
// (contrast of text over bgContainer ≥ 4.5, secondary ≥ 3.0 against the surface).
export const THEME_SURFACES: Record<ThemeMode, ThemeSurface> = {
  // UI v2 parchment (SPEC §3, §7.1). Replaces the legacy blue light theme.
  light: {
    primary: '#C2552B',
    bgContainer: '#FEFCF8',
    bgElevated: '#FEFCF8',
    bgLayout: '#FBF7F1',
    bgSpotlight: 'rgba(30, 24, 18, 0.9)',
    text: '#1E1812',
    textSecondary: '#4A4035',
    textTertiary: '#8A7C6E',
    border: '#E8DDD0',
    borderSecondary: '#EEE6D8',
    fillSecondary: 'rgba(30, 24, 18, 0.04)',
    fillTertiary: 'rgba(30, 24, 18, 0.02)',
    tableHeaderBg: '#FAF7F0', // rgba(245,239,229,.4) over bgContainer #FEFCF8 — opaque, so a sticky header hides what scrolls under it
    tableHeaderColor: '#8A7C6E',
    tableRowHoverBg: 'rgba(30, 24, 18, 0.025)',
    menuItemSelectedBg: '#FEFCF8',
    menuItemHoverBg: 'rgba(30, 24, 18, 0.04)',
    segmentedSelectedBg: '#FEFCF8',
    segmentedTrackBg: 'rgba(30, 24, 18, 0.04)',
    tagDefaultBg: 'rgba(30, 24, 18, 0.06)',
    progressRemaining: 'rgba(30, 24, 18, 0.06)',
    dividerColor: 'rgba(30, 24, 18, 0.08)',
    dropdownHoverBg: 'rgba(30, 24, 18, 0.04)',
    defaultBorderColor: '#E8DDD0',
    successColor: '#5A7A52',
    warningColor: '#C2552B',
    siderBg: '#F5EFE5',
    headerBg: '#FBF7F1',
  },
  // UI v2 "night kitchen" — dark parchment (SPEC §9.2). Espresso surfaces, cream
  // ink, terracotta accent held constant. Cream #F2E8D8 over espresso #1E1812 ≈ 13:1.
  dark: {
    primary: '#C2552B',
    bgContainer: '#1E1812',
    bgElevated: '#262019',
    bgLayout: '#17110B',
    bgSpotlight: 'rgba(251, 247, 241, 0.94)',
    text: '#F2E8D8',
    textSecondary: '#C9BBA8',
    textTertiary: '#9A8B78',
    border: 'rgba(242, 232, 216, 0.12)',
    borderSecondary: 'rgba(242, 232, 216, 0.06)',
    fillSecondary: 'rgba(242, 232, 216, 0.06)',
    fillTertiary: 'rgba(242, 232, 216, 0.03)',
    tableHeaderBg: '#241D15',
    tableHeaderColor: '#9A8B78',
    tableRowHoverBg: 'rgba(194, 85, 43, 0.14)',
    menuItemSelectedBg: 'rgba(194, 85, 43, 0.20)',
    menuItemHoverBg: 'rgba(242, 232, 216, 0.06)',
    segmentedSelectedBg: '#262019',
    segmentedTrackBg: 'rgba(242, 232, 216, 0.06)',
    tagDefaultBg: 'rgba(242, 232, 216, 0.08)',
    progressRemaining: 'rgba(242, 232, 216, 0.08)',
    dividerColor: 'rgba(242, 232, 216, 0.10)',
    dropdownHoverBg: 'rgba(242, 232, 216, 0.06)',
    defaultBorderColor: 'rgba(242, 232, 216, 0.16)',
    successColor: '#8AAF7E',
    warningColor: '#E0A860',
    siderBg: '#1A140E',
    headerBg: '#17110B',
  },
  // UI v2 parchment accent-preset: warm "mocha" parchment (light).
  coffee: {
    primary: '#7A5230',
    bgContainer: '#FDFAF2',
    bgElevated: '#FDFAF2',
    bgLayout: '#F7EFE2',
    bgSpotlight: 'rgba(36, 26, 15, 0.9)',
    text: '#241A0F',
    textSecondary: '#4E3C28',
    textTertiary: '#8C7455',
    border: '#E7D9C2',
    borderSecondary: '#F0E6D5',
    fillSecondary: 'rgba(36, 26, 15, 0.04)',
    fillTertiary: 'rgba(36, 26, 15, 0.02)',
    tableHeaderBg: '#F7F1E4', // rgba(239,227,208,.4) over bgContainer #FDFAF2
    tableHeaderColor: '#8C7455',
    tableRowHoverBg: 'rgba(36, 26, 15, 0.025)',
    menuItemSelectedBg: '#FDFAF2',
    menuItemHoverBg: 'rgba(36, 26, 15, 0.04)',
    segmentedSelectedBg: '#FDFAF2',
    segmentedTrackBg: 'rgba(36, 26, 15, 0.04)',
    tagDefaultBg: 'rgba(36, 26, 15, 0.06)',
    progressRemaining: 'rgba(36, 26, 15, 0.06)',
    dividerColor: 'rgba(36, 26, 15, 0.08)',
    dropdownHoverBg: 'rgba(36, 26, 15, 0.04)',
    defaultBorderColor: '#E7D9C2',
    successColor: '#5A7A52',
    warningColor: '#D5A33A',
    siderBg: '#EFE3D0',
    headerBg: '#F7EFE2',
  },
  // UI v2 parchment accent-preset: dark "night garden" — espresso surfaces with
  // a green tint and a sage accent. Cream-green ink #E4EFE0 on #16211A ≈ 12:1.
  'dark-forest': {
    primary: '#6E9463',
    bgContainer: '#16211A',
    bgElevated: '#1E2C23',
    bgLayout: '#0E170F',
    bgSpotlight: 'rgba(245, 248, 243, 0.94)',
    text: '#E4EFE0',
    textSecondary: '#B6C9B0',
    textTertiary: '#85977E',
    border: 'rgba(228, 239, 224, 0.12)',
    borderSecondary: 'rgba(228, 239, 224, 0.06)',
    fillSecondary: 'rgba(228, 239, 224, 0.06)',
    fillTertiary: 'rgba(228, 239, 224, 0.03)',
    tableHeaderBg: '#1B271E',
    tableHeaderColor: '#85977E',
    tableRowHoverBg: 'rgba(110, 148, 99, 0.16)',
    menuItemSelectedBg: 'rgba(110, 148, 99, 0.22)',
    menuItemHoverBg: 'rgba(228, 239, 224, 0.06)',
    segmentedSelectedBg: '#1E2C23',
    segmentedTrackBg: 'rgba(228, 239, 224, 0.06)',
    tagDefaultBg: 'rgba(228, 239, 224, 0.08)',
    progressRemaining: 'rgba(228, 239, 224, 0.08)',
    dividerColor: 'rgba(228, 239, 224, 0.10)',
    dropdownHoverBg: 'rgba(228, 239, 224, 0.06)',
    defaultBorderColor: 'rgba(228, 239, 224, 0.16)',
    successColor: '#8AAF7E',
    warningColor: '#E0A860',
    siderBg: '#121B14',
    headerBg: '#0E170F',
  },
  // UI v2 parchment accent-preset: cool "sage" parchment with a forest-green
  // accent (light). Forest #3D6B4A over white text ≈ 5:1 for primary buttons.
  kingdom: {
    primary: '#3D6B4A',
    bgContainer: '#FCFDF9',
    bgElevated: '#FCFDF9',
    bgLayout: '#F5F8F1',
    bgSpotlight: 'rgba(20, 30, 22, 0.9)',
    text: '#17231A',
    textSecondary: '#3B4B3F',
    textTertiary: '#7A8A7E',
    border: '#DCE6D8',
    borderSecondary: '#E8EFE3',
    fillSecondary: 'rgba(20, 30, 22, 0.04)',
    fillTertiary: 'rgba(20, 30, 22, 0.02)',
    tableHeaderBg: '#F4F7F0', // rgba(233,239,227,.4) over bgContainer #FCFDF9
    tableHeaderColor: '#7A8A7E',
    tableRowHoverBg: 'rgba(20, 30, 22, 0.025)',
    menuItemSelectedBg: '#FCFDF9',
    menuItemHoverBg: 'rgba(20, 30, 22, 0.04)',
    segmentedSelectedBg: '#FCFDF9',
    segmentedTrackBg: 'rgba(20, 30, 22, 0.04)',
    tagDefaultBg: 'rgba(20, 30, 22, 0.06)',
    progressRemaining: 'rgba(20, 30, 22, 0.06)',
    dividerColor: 'rgba(20, 30, 22, 0.08)',
    dropdownHoverBg: 'rgba(20, 30, 22, 0.04)',
    defaultBorderColor: '#DCE6D8',
    successColor: '#5A7A52',
    warningColor: '#D5A33A',
    siderBg: '#E9EFE3',
    headerBg: '#F5F8F1',
  },
};

// Semantic colors (shared across themes — intentionally consistent for a11y).
export const colors = {
  primary: { light: '#3b82f6', DEFAULT: '#2563eb', dark: '#60a5fa' },
  success: { light: 'rgba(5, 150, 105, 0.1)', DEFAULT: '#059669', text: '#059669', darkText: '#34d399' },
  warning: { light: 'rgba(217, 119, 6, 0.1)', DEFAULT: '#d97706', text: '#d97706', darkText: '#fbbf24' },
  error:   { light: 'rgba(220, 38, 38, 0.1)', DEFAULT: '#dc2626', text: '#dc2626', darkText: '#f87171' },
  info:    { light: 'rgba(8, 145, 178, 0.1)', DEFAULT: '#0891b2', text: '#0891b2', darkText: '#22d3ee' },
  revenue: { DEFAULT: '#059669', dark: '#34d399' },
  expense: { DEFAULT: '#dc2626', dark: '#f87171' },
} as const;

/*
 * A second `gradients` map used to sit here: nine literal hex pairs including
 * `#2563eb -> #6366f1` ("primary") and `#2563eb -> #8b5cf6` ("premium"), both
 * AntD/Tailwind blue on a parchment app. Every one of the nine had zero call
 * sites — measured 2026-09-24 — so they were deleted rather than re-tinted.
 * The live pair is the `gradients` export above, built from the theme tokens.
 */

export interface BrandingConfig {
  fontFamily: FontFamily;
  primaryColor: string;
  borderRadius: number;
}

export interface ThemeOptions {
  branding?: Partial<BrandingConfig>;
  mode?: ThemeMode;
}

export function getThemeSurface(mode: ThemeMode): ThemeSurface {
  return THEME_SURFACES[mode] ?? THEME_SURFACES.light;
}

// AntD `<Tag color="#hex">` always renders WHITE text. Some parchment tag
// backgrounds are too light for white to pass WCAG AA — currently the gold
// `#D5A33A` (white ≈ 2.2:1). For those, return espresso ink `#1E1812` (≈ 8:1,
// readable in both light and dark modes); otherwise return undefined so AntD's
// default text applies. Centralizes the contrast rule so tags never silently
// regress. Usage: `<Tag color={c} style={{ color: tagTextColor(c) }}>`.
const LIGHT_TAG_BACKGROUNDS = new Set<string>(['#D5A33A']);
export function tagTextColor(background?: string): string | undefined {
  return background && LIGHT_TAG_BACKGROUNDS.has(background) ? '#1E1812' : undefined;
}

export function createTheme(options?: ThemeOptions): ThemeConfig {
  const { branding, mode = 'light' } = options || {};
  const surface = getThemeSurface(mode);
  const {
    fontFamily,
    primaryColor = surface.primary,
    borderRadius: radius = 10,
  } = branding || {};
  // Geist by default; honour an explicit workspace branding font when set.
  // `lookupOwn`, not a bare index: `branding` is workspace settings off the
  // wire, so `FontFamily` carries no runtime guarantee and a stored
  // `fontFamily` of `'constructor'` would put a FUNCTION into antd's
  // `fontFamily` token. An unknown name falls back to the default stack.
  const resolvedFontFamily = lookupOwn(fontFamilies, fontFamily) ?? uiFontStack;

  const isDark = isDarkMode(mode);
  // No light/dark shadow branch any more: `shadows` is var() references and the
  // [data-theme] blocks in tokens.css resolve them per theme. `isDark` still
  // selects antd's algorithm, which is a different question.

  return {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: primaryColor,
      colorSuccess: surface.successColor ?? colors.success.DEFAULT,
      colorWarning: surface.warningColor ?? colors.warning.DEFAULT,
      colorError: colors.error.DEFAULT,
      colorInfo: colors.info.DEFAULT,

      borderRadius: radius,
      borderRadiusSM: 6,
      borderRadiusLG: 14,
      borderRadiusXS: 4,

      fontFamily: resolvedFontFamily,
      fontSize: 13,
      fontSizeHeading1: 38,
      fontSizeHeading2: 30,
      fontSizeHeading3: 24,
      fontSizeHeading4: 20,
      fontSizeHeading5: 16,
      lineHeight: 1.6,
      fontWeightStrong: 600,

      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 48,

      motionDurationFast: '150ms',
      motionDurationMid: '200ms',
      motionDurationSlow: '300ms',
      motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      motionEaseOut: 'cubic-bezier(0, 0, 0.2, 1)',

      colorBgContainer: surface.bgContainer,
      colorBgElevated: surface.bgElevated,
      colorBgLayout: surface.bgLayout,
      colorBgSpotlight: surface.bgSpotlight,
      colorBorder: surface.border,
      colorBorderSecondary: surface.borderSecondary,
      colorFillSecondary: surface.fillSecondary,
      colorFillTertiary: surface.fillTertiary,
      colorText: surface.text,
      colorTextSecondary: surface.textSecondary,
      colorTextTertiary: surface.textTertiary,
      boxShadow: shadows.card,
      boxShadowSecondary: shadows.sm,
    },
    components: {
      Layout: {
        siderBg: surface.siderBg ?? surface.bgLayout,
        headerBg: surface.headerBg ?? surface.bgLayout,
        bodyBg: surface.bgLayout,
      },
      Button: {
        borderRadius: radius,
        controlHeight: 40,
        controlHeightLG: 48,
        controlHeightSM: 32,
        paddingContentHorizontal: 20,
        fontWeight: 500,
        primaryShadow: shadows.button,
        defaultBorderColor: surface.defaultBorderColor,
      },
      Card: {
        borderRadiusLG: radius + 4,
        paddingLG: spacing.lg,
        boxShadowTertiary: shadows.card,
        headerFontSize: 12.5,
      },
      Input: {
        borderRadius: radius,
        controlHeight: 40,
        controlHeightLG: 48,
        paddingInline: 14,
        activeShadow: shadows.inputFocus,
      },
      Select: {
        borderRadius: radius,
        controlHeight: 40,
        controlHeightLG: 48,
      },
      Table: {
        borderRadius: radius + 4,
        headerBg: surface.tableHeaderBg,
        headerColor: surface.tableHeaderColor,
        rowHoverBg: surface.tableRowHoverBg,
        headerSplitColor: 'transparent',
        cellPaddingBlock: 14,
        cellPaddingInline: 16,
      },
      Modal: {
        borderRadiusLG: radius + 10,
        paddingContentHorizontal: 24,
        paddingMD: 24,
        titleFontSize: 18,
        headerBg: surface.bgElevated,
        contentBg: surface.bgElevated,
      },
      Drawer: {
        borderRadiusLG: radius + 4,
      },
      Menu: {
        itemBorderRadius: radius,
        itemMarginBlock: 4,
        itemMarginInline: 8,
        itemPaddingInline: 16,
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemSelectedBg: surface.menuItemSelectedBg,
        itemSelectedBg: surface.menuItemSelectedBg,
        itemHoverBg: surface.menuItemHoverBg,
        darkItemHoverBg: surface.menuItemHoverBg,
      },
      Tabs: {
        inkBarColor: primaryColor,
        itemSelectedColor: primaryColor,
        titleFontSize: 14,
        cardGutter: 4,
      },
      Tag: {
        borderRadiusSM: 6,
        defaultBg: surface.tagDefaultBg,
      },
      Tooltip: {
        borderRadius: radius,
        paddingSM: 8,
        paddingXS: 6,
      },
      Dropdown: {
        borderRadiusLG: radius + 4,
        paddingBlock: 6,
        controlItemBgHover: surface.dropdownHoverBg,
      },
      Alert: {
        borderRadiusLG: radius + 4,
      },
      Message: {
        borderRadiusLG: radius,
      },
      Notification: {
        borderRadiusLG: radius + 4,
      },
      Progress: {
        remainingColor: surface.progressRemaining,
      },
      Segmented: {
        borderRadius: radius,
        borderRadiusSM: 6,
        itemSelectedBg: surface.segmentedSelectedBg,
        trackBg: surface.segmentedTrackBg,
      },
      Statistic: {
        titleFontSize: 13,
        contentFontSize: 28,
      },
      DatePicker: {
        borderRadius: radius,
        cellHeight: 28,
        cellWidth: 36,
      },
      Checkbox: {
        borderRadiusSM: 4,
      },
      Radio: {
        buttonSolidCheckedBg: primaryColor,
      },
      Switch: {
        trackHeight: 24,
        trackMinWidth: 44,
        handleSize: 20,
      },
      Divider: {
        colorSplit: surface.dividerColor,
      },
    },
  };
}

export const defaultTheme = createTheme();
export const darkTheme = createTheme({ mode: 'dark' });
